/*
 * @Author: caiyuliu
 * @Date: 2025-12-15 09:46:13
 * @LastEditors: caiyuliu
 * @LastEditTime: 2025-12-15 10:04:22
 * @Description: 协议数据提取工具 - 从路由配置中提取协议信息
 */

/**
 * 从路由配置中提取协议数据
 * @param {Array} routes - 路由配置数组
 * @returns {Object} 提取的协议数据
 */
export function extractProtocolData(routes) {
  const protocolRoute = routes.find(r => r.path === '/protocol')
  if (!protocolRoute || !protocolRoute.children) {
    return getEmptyData()
  }

  const protocols = protocolRoute.children
  const products = categorizeByProduct(protocols)
  const statistics = generateStatistics(products, protocols)
  const versions = extractVersionInfo(protocols)

  return {
    products,
    statistics,
    versions,
    allProtocols: protocols
  }
}

/**
 * 按产品分类协议
 */
function categorizeByProduct(protocols) {
  const productMap = {
    teachingAide: {
      id: 'teachingAide',
      name: '授课助手',
      nameEn: 'Teaching Aide',
      icon: '📚',
      color: '#4CAF50',
      protocols: []
    },
    whiteBoard: {
      id: 'whiteBoard',
      name: '白板2软件',
      nameEn: 'WhiteBoard',
      icon: '✏️',
      color: '#2196F3',
      protocols: []
    },
    amdoxSystem: {
      id: 'amdoxSystem',
      name: '校园综合后台',
      nameEn: 'Amdox System',
      icon: '🏫',
      color: '#FF9800',
      protocols: []
    },
    eduCloud: {
      id: 'eduCloud',
      name: '门户',
      nameEn: 'EduCloud Portal',
      icon: '🌐',
      color: '#9C27B0',
      protocols: []
    },
    rubikCube: {
      id: 'rubikCube',
      name: '魔方',
      nameEn: 'Rubik Cube',
      icon: '🎲',
      color: '#F44336',
      protocols: []
    },
    android: {
      id: 'android',
      name: '安卓',
      nameEn: 'Android',
      icon: '📱',
      color: '#00BCD4',
      protocols: []
    },
    threeClass: {
      id: 'threeClass',
      name: '三个课堂',
      nameEn: 'Three Class',
      icon: '🎓',
      color: '#673AB7',
      protocols: []
    },
    control: {
      id: 'control',
      name: '控制软件',
      nameEn: 'Control Software',
      icon: '⚙️',
      color: '#607D8B',
      protocols: []
    }
  }

  protocols.forEach(protocol => {
    const { path, name } = protocol
    const comment = extractComment()

    // 判断属于哪个产品
    let productId = null
    if (path.includes('teachingAide') || name.includes('teachingAide')) {
      productId = 'teachingAide'
    } else if (path.includes('whiteBoard') || name.includes('whiteBoard')) {
      productId = 'whiteBoard'
    } else if (path.includes('amdoxSystem') || name.includes('amdoxSystem')) {
      productId = 'amdoxSystem'
    } else if (path.includes('eduCloud') || name.includes('eduCloud')) {
      productId = 'eduCloud'
    } else if (path.includes('rubikCube') || name.includes('rubikCube')) {
      productId = 'rubikCube'
    } else if (path.includes('android') || name.includes('android')) {
      productId = 'android'
    } else if (path.includes('threeClass') || name.includes('threeClass')) {
      productId = 'threeClass'
    } else if (path.includes('control') || name.includes('control')) {
      productId = 'control'
    }

    if (productId && productMap[productId]) {
      const protocolInfo = {
        path: `/protocol/${path}`,
        name,
        type: getProtocolType(path, name),
        typeName: getProtocolTypeName(path, name),
        version: extractVersionFromPath(path),
        language: extractLanguage(path, name),
        comment
      }
      productMap[productId].protocols.push(protocolInfo)
    }
  })

  // 只返回有协议的产品
  return Object.values(productMap).filter(p => p.protocols.length > 0)
}

/**
 * 提取协议类型
 */
function getProtocolType(path, name) {
  const lowerPath = path.toLowerCase()
  const lowerName = name.toLowerCase()

  if (lowerPath.includes('disclaimer') || lowerName.includes('disclaimer')) {
    return 'disclaimer'
  } else if (lowerPath.includes('privacy') || lowerName.includes('privacy')) {
    return 'privacy'
  } else if (lowerPath.includes('useragreement') || lowerName.includes('useragreement')) {
    return 'userAgreement'
  } else if (lowerPath.includes('organize') || lowerName.includes('organize')) {
    return 'organize'
  } else if (lowerPath.includes('install') || lowerName.includes('install')) {
    return 'install'
  }
  return 'other'
}

/**
 * 获取协议类型中文名
 */
function getProtocolTypeName(path, name) {
  const type = getProtocolType(path, name)
  const typeMap = {
    disclaimer: '免责声明',
    privacy: '隐私政策',
    userAgreement: '用户协议',
    organize: '组织认证',
    install: '安装软件',
    other: '其他'
  }
  return typeMap[type] || '其他'
}

/**
 * 从路径中提取版本号
 */
function extractVersionFromPath(path) {
  // 匹配 2.0, 2.1 等版本号
  const versionMatch = path.match(/[-_](\d+\.\d+)/)
  if (versionMatch) {
    return versionMatch[1]
  }
  return '1.0'
}

/**
 * 提取语言信息
 */
function extractLanguage(path, name) {
  const langMap = {
    'ar-SA': '阿拉伯语',
    'bn-BD': '孟加拉语',
    'es-ES': '西班牙语',
    'en-US': '英语',
    'fr-FR': '法语',
    'hi-IN': '印地语',
    'id-ID': '印尼语',
    'ja-JP': '日语',
    'ko-KR': '韩语',
    'ms-MY': '马来语',
    'ru-RU': '俄语',
    'th-TH': '泰语',
    'vi-VN': '越南语',
    'zh-CN': '简体中文',
    EN: '英语',
    CN: '简体中文'
  }

  for (const [code, lang] of Object.entries(langMap)) {
    if (path.includes(code) || name.includes(code)) {
      return { code, name: lang }
    }
  }

  return { code: 'zh-CN', name: '简体中文' }
}

/**
 * 提取注释信息
 */
function extractComment() {
  // 这里可以根据需要添加更详细的注释提取逻辑
  return ''
}

/**
 * 生成统计数据
 */
function generateStatistics(products, protocols) {
  const protocolTypes = new Set()
  const languages = new Set()

  protocols.forEach(p => {
    const type = getProtocolType(p.path, p.name)
    protocolTypes.add(type)

    const lang = extractLanguage(p.path, p.name)
    languages.add(lang.code)
  })

  // 统计各类型数量
  const typeDistribution = {}
  protocols.forEach(p => {
    const typeName = getProtocolTypeName(p.path, p.name)
    typeDistribution[typeName] = (typeDistribution[typeName] || 0) + 1
  })

  return {
    totalProtocols: protocols.length,
    totalProducts: products.length,
    totalLanguages: languages.size,
    protocolTypes: Array.from(protocolTypes),
    languages: Array.from(languages),
    typeDistribution,
    lastUpdate: new Date().toISOString().split('T')[0]
  }
}

/**
 * 提取版本信息
 */
function extractVersionInfo(protocols) {
  const versionMap = new Map()

  protocols.forEach(protocol => {
    const version = extractVersionFromPath(protocol.path)
    const productMatch = protocol.path.match(/^(\w+)/)
    const product = productMatch ? productMatch[1] : 'unknown'

    const key = `${product}-${version}`
    if (!versionMap.has(key)) {
      versionMap.set(key, {
        product,
        version,
        protocols: [],
        // 这里可以添加实际的更新日期，目前使用占位符
        updateDate: version === '2.1' ? '2024-10-30' : version === '2.0' ? '2023-12-19' : '2023-03-29'
      })
    }

    versionMap.get(key).protocols.push(protocol)
  })

  return Array.from(versionMap.values()).sort((a, b) => {
    return new Date(b.updateDate) - new Date(a.updateDate)
  })
}

/**
 * 返回空数据结构
 */
function getEmptyData() {
  return {
    products: [],
    statistics: {
      totalProtocols: 0,
      totalProducts: 0,
      totalLanguages: 0,
      protocolTypes: [],
      languages: [],
      typeDistribution: {},
      lastUpdate: new Date().toISOString().split('T')[0]
    },
    versions: [],
    allProtocols: []
  }
}

export default {
  extractProtocolData
}
